import type { CreatorAssessment } from '@/types/portal';

/**
 * Pre-computed AI vetting outcomes, keyed by creator id. In production the
 * assessment is produced on demand by a review job that reads every signal the
 * platform holds about the creator; here the outcomes are materialised so the
 * profile can render them without a backend.
 */
export const creatorAssessments: Record<string, CreatorAssessment> = {
  'crt-zawadi': {
    verdict: 'Ready to contract',
    tone: 'positive',
    stars: 5,
    summary:
      'A strong, low-risk hire. Identity, KYC and wallet are all in good standing, engagement sits well above the agency benchmark, and the delivery record is reliable. Safe to contract at the quoted rate.',
    strengths: [
      'Identity verified, KYC complete and the M-Pesa wallet is active',
      'Engagement of 6.8% is comfortably above the 6% agency benchmark',
      'Six prior campaigns delivered at 92% on time',
    ],
    risks: [
      'Engagement has eased from a 7.4% TikTok peak — worth watching across the brief',
      'Rate sits at the top of the micro tier, so budget accordingly',
    ],
    model: 'Claude AI',
  },
  'crt-sanaa': {
    verdict: 'Ready to contract',
    tone: 'positive',
    stars: 5,
    summary:
      'The most dependable delivery record in the cohort. Compliance is clean across the board and the audience converts, making this a straightforward contract.',
    strengths: [
      'Eleven campaigns delivered at 96% on time — the strongest record in the pool',
      'Engagement of 7.4% with high saves and shares from a loyal food audience',
      'KYC complete, wallet active and brand safety clear',
    ],
    risks: [
      'Instagram following is small at 5.4K, so reach is concentrated on TikTok',
      'Secondary category sits close to beauty — confirm the skincare fit before briefing',
    ],
    model: 'Claude AI',
  },
  'crt-neema': {
    verdict: 'Proceed with caution',
    tone: 'caution',
    stars: 3,
    summary:
      'Reach and authority are excellent, but this creator cannot be contracted yet. KYC verification is incomplete, which blocks payouts, and the delivery sample is thin.',
    strengths: [
      'Authoritative fintech explainer with 128K across YouTube, TikTok and X',
      'Unusually strong YouTube watch-through for money topics',
      'Genuine multi-market coverage across Kenya and Uganda',
    ],
    risks: [
      'KYC is incomplete — contracting cannot be finalised until the ID is verified',
      'Engagement of 4.1% sits below the agency benchmark',
      'Only three prior campaigns, so the delivery record is still limited',
    ],
    model: 'Claude AI',
  },
  'crt-jabari': {
    verdict: 'Proceed with caution',
    tone: 'caution',
    stars: 3,
    summary:
      'A verified, well-liked wellness creator with a sound delivery history — but the payout wallet is inactive, so funds cannot be released until that is resolved.',
    strengths: [
      'Verified wellness specialist with a consistent weekly cadence',
      'Five campaigns delivered at 90% on time',
      'Solid long-form retention on YouTube for explainer formats',
    ],
    risks: [
      'Payout wallet is inactive after a failed settlement — resolve before contracting',
      'Category tag is duplicated on the profile and needs cleaning up',
      'Engagement of 5.9% is modest against the wider cohort',
    ],
    model: 'Claude AI',
  },
  'crt-halima': {
    verdict: 'Ready to contract',
    tone: 'positive',
    stars: 4,
    summary:
      'The strongest verified reach in the Tanzanian pool, suited to brand-film briefs. Compliance is clean; the only real consideration is the premium rate.',
    strengths: [
      'Largest verified Instagram reach in the Tanzanian pool at 142K',
      'Cinematic long-form well matched to heritage and brand storytelling',
      'Seven campaigns delivered at 94% on time',
    ],
    risks: [
      'Premium rate puts this at the top of the macro tier',
      'Engagement of 4.6% trails the micro and nano cohort',
      'Slower content cadence — expect longer lead times on delivery',
    ],
    model: 'Claude AI',
  },
  'crt-tunde': {
    verdict: 'Proceed with caution',
    tone: 'caution',
    stars: 3,
    summary:
      'Best used for scale rather than conversion. Reach is enormous, but engagement is the lowest in the cohort and the delivery record is the weakest.',
    strengths: [
      'Biggest raw TikTok distribution in the region with 318K followers',
      'Strong for hype moments, launches and event amplification',
      'Music-audience overlap that suits festival and nightlife briefs',
    ],
    risks: [
      'Engagement of 3.2% is the lowest in the cohort',
      'On-time delivery sits at 82%, the weakest track record reviewed',
      'Occasional late delivery already flagged on the account',
    ],
    model: 'Claude AI',
  },
  'crt-amina-k': {
    verdict: 'Ready to contract',
    tone: 'positive',
    stars: 4,
    summary:
      'The clear lead for Kinshasa street style. Compliance is clean and the bilingual audience is a genuine asset for premium lifestyle launches.',
    strengths: [
      'Dominant Kinshasa fashion reach with 118K on Instagram',
      'Bilingual French and Lingala audience suited to premium launches',
      'Early engagements delivered at 91% on time',
    ],
    risks: [
      'Only two prior campaigns, so the delivery sample is small',
      'TikTok account is not verified',
      'Market concentration in DR Congo limits regional scaling',
    ],
    model: 'Claude AI',
  },
  'crt-david': {
    verdict: 'Ready to contract',
    tone: 'positive',
    stars: 4,
    summary:
      'A dependable mid-tier option for East African reach. Compliance is clean and delivery is consistent, though the niche drifts from beauty.',
    strengths: [
      'Reliable Uganda and Kenya coverage across two connected platforms',
      'Ninety-three percent on-time delivery across four campaigns',
      'Documentary-lite storytelling suited to consideration-led briefs',
    ],
    risks: [
      'YouTube channel is unverified and small at 20K',
      'Rate sits mid-tier relative to total reach',
      'Travel niche may drift from skincare positioning',
    ],
    model: 'Claude AI',
  },
  'crt-furaha': {
    verdict: 'Proceed with caution',
    tone: 'caution',
    stars: 4,
    summary:
      'Exceptional engagement and a spotless delivery record, but identity verification is still pending and reach is small — best paired with larger creators for scale.',
    strengths: [
      'Highest engagement in the cohort at 8.1%',
      'Eight campaigns delivered at 97% on time',
      'Nano-tier cost efficiency with a highly trusting community',
    ],
    risks: [
      'Identity is still Pending review — confirm verification before contracting',
      'Both platform accounts are unverified',
      'Reach of 24.9K is small, so pair with larger creators for scale',
    ],
    model: 'Claude AI',
  },
  'crt-grace': {
    verdict: 'Ready to contract',
    tone: 'positive',
    stars: 5,
    summary:
      'A polished, low-risk creator with a purchase-ready audience. Compliance is clean and delivery is consistently ahead of schedule.',
    strengths: [
      'Premium home aesthetic with high production quality',
      'Five campaigns delivered at 95% on time',
      'Audience actively investing in their spaces, signalling high purchase intent',
    ],
    risks: [
      'Engagement of 5.0% sits mid-range for the cohort',
      'Interiors category skews away from beauty — check brand adjacency',
      'Long-form cadence is slower than short-form peers',
    ],
    model: 'Claude AI',
  },
  'crt-emmanuel': {
    verdict: 'Proceed with caution',
    tone: 'caution',
    stars: 3,
    summary:
      'A large, fast-moving Uganda sports audience, but reliability is the concern. On-time delivery is among the lowest reviewed and engagement trails the benchmark.',
    strengths: [
      'Large Uganda sports reach with 268K on TikTok',
      'Strong live-event and match-day amplification',
      'Young male audience that fits sport and beverage briefs',
    ],
    risks: [
      'On-time delivery at 85% is among the weakest reviewed',
      'Engagement of 3.9% sits below the agency benchmark',
      'Only three prior campaigns, so the record is still thin',
    ],
    model: 'Claude AI',
  },
  'crt-brian': {
    verdict: 'Proceed with caution',
    tone: 'caution',
    stars: 3,
    summary:
      'A capable micro-tier creator, but there is an open exception: a deliverable is currently missing on an active campaign, and on-time delivery sits just under the bar.',
    strengths: [
      'Reliable micro-tier reach with punchy short-form Reels',
      'Nine campaigns of platform experience',
      'Strong Reels performance that travels beyond the follower count',
    ],
    risks: [
      'A deliverable is currently missing on an active campaign',
      'On-time delivery of 89% sits below the 90% bar',
      'TikTok account is not verified',
    ],
    model: 'Claude AI',
  },
};